import React, { useState } from "react";
import { motion } from "framer-motion";

const ContactUsSection = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <section id="contact" className="py-20 sm:py-24 bg-white overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-2 gap-20 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30, z: 0 }}
            whileInView={{ opacity: 1, x: 0, z: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ willChange: "transform, opacity" }}
          >
            <div className="text-blue-600 bg-blue-50 border border-blue-600/20 font-black text-xs mb-6 px-5 py-2 w-fit rounded-full uppercase tracking-[0.2em] shadow-sm">
              Get In Touch
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-[1.1] mb-8 tracking-tight">
              Ready to Print? <br />
              <span className="text-blue-600">Let's Talk.</span>
            </h2>

            <p className="text-gray-500 text-[16px] sm:text-lg leading-relaxed mb-10 font-medium">
              Tell us about your label requirements and get a customized quote
              within 24 hours. Our team is ready to help you find the perfect
              solution.
            </p>

            <ul className="space-y-8">
              {[
                {
                  icon: "📍",
                  label: "Address",
                  content: (
                    <>
                      Sahajink PVT LTD,
                      <br />
                      Industrial Area, Phase II,
                      <br />
                      Surat, Gujarat – 395006
                    </>
                  ),
                },
                {
                  icon: "📞",
                  label: "Phone",
                  content: (
                    <a
                      href="tel:+916352424442"
                      className="text-blue-600 hover:text-blue-500 font-bold"
                    >
                      +91 6352424442
                    </a>
                  ),
                },
                {
                  icon: "✉️",
                  label: "Email",
                  content: (
                    <a
                      href="mailto:connect@sahajink.com"
                      className="text-blue-600 hover:text-blue-500 font-bold"
                    >
                      connect@sahajink.com
                    </a>
                  ),
                },
                {
                  icon: "🕐",
                  label: "Working Hours",
                  content: "Mon – Sat: 9:00 AM – 6:00 PM",
                },
              ].map((item, idx) => (
                <motion.li
                  key={item.label}
                  initial={{ opacity: 0, y: 10, z: 0 }}
                  whileInView={{ opacity: 1, y: 0, z: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + idx * 0.1 }}
                  style={{ willChange: "transform, opacity" }}
                  className="flex gap-5"
                >
                  <div className="w-12 h-12 bg-blue-50 border border-blue-600/10 rounded-2xl flex items-center justify-center text-xl shadow-inner">
                    {item.icon}
                  </div>

                  <div className="text-gray-500 text-sm">
                    <strong className="block text-gray-900 mb-1 font-black uppercase tracking-widest text-[10px]">
                      {item.label}
                    </strong>
                    <div className="font-medium text-[15px] leading-relaxed">
                      {item.content}
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30, z: 0 }}
            whileInView={{ opacity: 1, x: 0, z: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ willChange: "transform, opacity" }}
            className="bg-gray-50/50 border border-gray-100 rounded-[30px] sm:rounded-[40px] p-8 md:p-12 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.05)] relative overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              {/* Name */}
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">
                  Full Name *
                </label>

                <input
                  type="text"
                  required
                  placeholder="Your full name"
                  className="bg-white border border-gray-100 rounded-2xl px-6 py-4 text-base font-medium focus:border-blue-600 focus:ring-4 focus:ring-blue-600/5 outline-none transition-all placeholder:text-gray-300 shadow-sm"
                />
              </div>

              {/* Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">
                    Phone Number *
                  </label>

                  <input
                    type="tel"
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className="bg-white border border-gray-100 rounded-2xl px-6 py-4 text-base font-medium focus:border-blue-600 focus:ring-4 focus:ring-blue-600/5 outline-none transition-all placeholder:text-gray-300 shadow-sm"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">
                    Email Address *
                  </label>

                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="bg-white border border-gray-100 rounded-2xl px-6 py-4 text-base font-medium focus:border-blue-600 focus:ring-4 focus:ring-blue-600/5 outline-none transition-all placeholder:text-gray-300 shadow-sm"
                  />
                </div>
              </div>

              {/* Industry */}
              <div className="flex flex-col gap-3">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">
                  Industry
                </label>

                <select className="bg-white border border-gray-100 rounded-2xl px-6 py-4 text-base font-medium focus:border-blue-600 focus:ring-4 focus:ring-blue-600/5 outline-none transition-all shadow-sm appearance-none cursor-pointer">
                  <option value="">Select your industry</option>
                  <option>Food & Beverage</option>
                  <option>Pharmaceutical</option>
                  <option>Cosmetics & Beauty</option>
                  <option>FMCG & Retail</option>
                  <option>Logistics</option>
                  <option>Other</option>
                </select>
              </div>

              {/* Message */}
              <div className="flex flex-col gap-3">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">
                  Message / Requirements *
                </label>

                <textarea
                  rows="4"
                  required
                  placeholder="Describe your label requirements..."
                  className="bg-white border border-gray-100 rounded-2xl px-6 py-4 text-base font-medium resize-none min-h-[140px] focus:border-blue-600 focus:ring-4 focus:ring-blue-600/5 outline-none transition-all placeholder:text-gray-300 shadow-sm"
                />
              </div>

              {/* Button */}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-black py-5 rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 uppercase tracking-widest text-sm"
              >
                Request a Quote
              </button>

              {/* Success Message */}
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-5 bg-green-50 border border-green-200 rounded-2xl text-green-700 font-bold text-center text-sm shadow-sm"
                >
                  ✅ Thank you! We'll get back to you within 24 hours.
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ContactUsSection;
